/**
 * @file index.jsx
 * @author Rishikesh
 * @date 2024-09-09
 * @description Navigation routings of this app.
 */
import { createBrowserRouter, Navigate } from "react-router-dom";
import { useSelector } from "react-redux";

import AppLayout from "../components/layout/AppLayout";
import LoginPage from "../pages/auth/LoginPage";
import CandidateLoginPage from "../pages/auth/CandidateLoginPage";
import MainLayout from "../components/layout/MainLayout";
import DashboardPage from "../pages/DashboardPage";
import UsersPage from "../pages/UsersPage";
import LeadsPage from "../pages/LeadsPage";
import CandidatesPage from "../pages/CandidatesPage";
import SettingsPage from "../pages/SettingsPage";
import ErrorBoundary from "../components/common/ErrorBoundary";
import ManageUser from "../pages/UsersPage/ManageUser";
import MasterSettingsPage from "../pages/MasterSettingsPage";
import Courses from "../pages/MasterSettingsPage/Courses";
import ManageCourse from "../pages/MasterSettingsPage/Courses/ManageCourse";
import Languages from "../pages/MasterSettingsPage/Languages";
import ManageLanguage from "../pages/MasterSettingsPage/Languages/ManageLanguage";
import Documents from "../pages/MasterSettingsPage/Documents";
import ManageDocument from "../pages/MasterSettingsPage/Documents/ManageDocument";
import ManageLead from "../pages/LeadsPage/ManageLead";
import LeadDetails from "../pages/LeadsPage/LeadDetails";
import CandidateDetails from "../pages/CandidatesPage/CandidateDetails";
import CandidateLifecycleAgent from "../pages/CandidatesPage/CandidateLifecycleAgent";
import CandidateLifecycle from "../pages/CandidatesPage/CandidateLifecycle";
import ResetPassword from "../pages/auth/ResetPassword";
import ForgotPassword from "../pages/auth/ForgotPassword";
import CandidateResetPassword from "../pages/auth/CandidateResetPassword";
import CandidateForgotPassword from "../pages/auth/CandidateForgotPassword";
import CandidateDashboardPage from "../pages/CandidateDashboardPage";
import ReportPage from "../pages/ReportPage";
import ImportExcelPage from "../pages/ImportExcelPage";

// ProtectedRoute component
const ProtectedRoute = ({ element }) => {
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  return isAuthenticated ? element : <Navigate to="/" replace />;
};

// PublicRoute component
const PublicRoute = ({ element }) => {
  const isAuthenticated = useSelector(
    (state) => state.auth.isAuthenticated && state.auth.user?.role
  );
  return isAuthenticated ? <Navigate to="/dashboard" replace /> : element;
};

// Router configuration
export const router = createBrowserRouter(
  [
    {
      path: "/",
      element: <AppLayout />,
      children: [
        {
          index: true,
          element: <PublicRoute element={<LoginPage />} />,
        },
        {
          path: "/candidate-login",
          element: <PublicRoute element={<CandidateLoginPage />} />,
        },
        {
          path: "/candidate-dashboard",
          element: <PublicRoute element={<CandidateDashboardPage />} />,
        },
        {
          path: "candidate-forgotpassword",
          element: <PublicRoute element={<CandidateForgotPassword />} />,
        },
        {
          path: "candidate-resetpassword/:id",
          element: <PublicRoute element={<CandidateResetPassword />} />,
        },
        {
          path: "forgotpassword",
          element: <PublicRoute element={<ForgotPassword />} />,
        },
        {
          path: "resetpassword/:id",
          element: <PublicRoute element={<ResetPassword />} />,
        },
        {
          path: "dashboard",
          element: <ProtectedRoute element={<MainLayout />} />,
          children: [
            {
              index: true,
              element: <DashboardPage />,
            },
          ],
        },
        {
          path: "master-settings",
          element: <ProtectedRoute element={<MainLayout />} />,
          children: [
            {
              index: true,
              element: <MasterSettingsPage />,
            },
            {
              path: "courses",
              children: [
                {
                  index: true,
                  element: <Courses />,
                },
                {
                  path: "manage-course/:id",
                  element: <ManageCourse />,
                },
              ],
            },
            {
              path: "languages",
              children: [
                {
                  index: true,
                  element: <Languages />,
                },
                {
                  path: "manage-language/:id",
                  element: <ManageLanguage />,
                },
              ],
            },
            {
              path: "documents",
              children: [
                {
                  index: true,
                  element: <Documents />,
                },
                {
                  path: "manage-document/:id",
                  element: <ManageDocument />,
                },
              ],
            },
          ],
        },
        {
          path: "users",
          element: <ProtectedRoute element={<MainLayout />} />,
          children: [
            {
              index: true,
              element: <UsersPage />,
            },
            {
              path: "manage-user/:id",
              element: <ManageUser />,
            },
          ],
        },
        {
          path: "leads",
          element: <ProtectedRoute element={<MainLayout />} />,
          children: [
            {
              index: true,
              element: <LeadsPage />,
            },
            {
              path: "manage-lead/:id",
              element: <ManageLead />,
            },
            {
              path: "lead-details/:id",
              element: <LeadDetails />,
            },
          ],
        },
        {
          path: "candidates",
          element: <ProtectedRoute element={<MainLayout />} />,
          children: [
            {
              index: true,
              element: <CandidatesPage />,
            },
            {
              path: "candidate-details/:id",
              element: <CandidateDetails />,
            },
            {
              path: "candidate-lifecycle/:id",
              element: <CandidateLifecycle />,
            },
            {
              path: "candidate-lifecycle-agent/:id",
              element: <CandidateLifecycleAgent />,
            },
          ],
        },
        {
          path: "report",
          element: <ProtectedRoute element={<MainLayout />} />,
          children: [
            {
              index: true,
              element: <ReportPage />,
            },
          ],
        },
        {
          path: "import-excel",
          element: <ProtectedRoute element={<MainLayout />} />,
          children: [
            {
              index: true,
              element: <ImportExcelPage />,
            },
          ],
        },
        {
          path: "settings",
          element: <ProtectedRoute element={<MainLayout />} />,
          children: [
            {
              index: true,
              element: <SettingsPage />,
            },
          ],
        },
      ],
    },
  ],
  {
    basename: "/",
    errorElement: <ErrorBoundary />,
  }
);
